import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { logout } from '../../../utils/auth/session';
import { NAVBAR_MENU_KEYS, NS } from '../../../i18n/constants/keys';
import { translateRole } from '../../../i18n/helpers/translateRole';
import { getImageSrc } from '../../../utils/helpers/image-utils';
import {
  ChevronRightIcon,
  LockKeyholeIcon,
  LogoutIcon,
  UserIcon,
} from '../../../icons/animated';
import ViewOption from '../ViewOption';

const SPRING = {
  type: 'spring',
  stiffness: 400,
  damping: 32,
  mass: 0.8,
};

const contentAnimations = {
  initial: { opacity: 0, filter: 'blur(8px)', y: 4 },
  animate: {
    opacity: 1,
    filter: 'blur(0px)',
    y: 0,
    transition: { delay: 0.12, duration: 0.25 },
  },
  exit: {
    opacity: 0,
    filter: 'blur(6px)',
    y: 4,
    transition: { duration: 0.1 },
  },
};

interface UserData {
  name: string;
  avatar: string;
  initials: string;
  role: string;
  email: string;
}

function Avatar({
  large = false,
  user,
}: {
  large?: boolean;
  user: UserData;
}) {
  const hasAvatar =
    Boolean(user.avatar) &&
    user.avatar !== 'null' &&
    user.avatar !== 'undefined' &&
    user.avatar.trim() !== '';

  return (
    <motion.div
      layoutId="hiacuity-user-avatar"
      transition={SPRING}
      className={`relative flex items-center justify-center rounded-full select-none ${
        large ? 'w-16 h-16 text-lg font-bold shadow-md' : 'w-10 h-10 text-sm font-semibold'
      } bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 text-white shadow-inner ring-2 ring-white/70 overflow-hidden flex-shrink-0`}
    >
      {hasAvatar ? (
        <img
          src={getImageSrc(user.avatar, '')}
          alt={user.name}
          className="w-full h-full object-cover"
        />
      ) : (
        <span>{user.initials}</span>
      )}
      {/* Online indicator */}
      <span
        className={`absolute bottom-0 right-0 ${
          large ? 'w-3.5 h-3.5 border-2' : 'w-2.5 h-2.5 border'
        } bg-emerald-400 border-white rounded-full`}
      />
    </motion.div>
  );
}

function MenuItem({
  icon,
  title,
  description,
  onClick,
  isDestructive = false,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  onClick: () => void;
  isDestructive?: boolean;
}) {
  return (
    <div
      onClick={onClick}
      className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer select-none transition-all duration-150 ${
        isDestructive
          ? 'hover:bg-red-50/80 active:bg-red-100/80 text-red-600'
          : 'hover:bg-slate-50 active:bg-slate-100 text-slate-800'
      }`}
    >
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
          isDestructive
            ? 'bg-red-100/70 text-red-600 group-hover:bg-red-200/70'
            : 'bg-slate-100 text-slate-600 group-hover:bg-emerald-50 group-hover:text-emerald-700'
        }`}
      >
        {icon}
      </div>

      <div className="flex-1 min-w-0 text-left">
        <p
          className={`text-xs font-semibold truncate ${
            isDestructive
              ? 'text-red-600'
              : 'text-slate-800 group-hover:text-emerald-700'
          }`}
        >
          {title}
        </p>
        {description && (
          <p
            className={`text-[11px] truncate mt-0.5 ${
              isDestructive ? 'text-red-400' : 'text-slate-400 group-hover:text-slate-500'
            }`}
          >
            {description}
          </p>
        )}
      </div>

      {!isDestructive && (
        <div className="flex-shrink-0 text-slate-300 group-hover:text-emerald-500 group-hover:translate-x-0.5 transition-all">
          <ChevronRightIcon size={14} />
        </div>
      )}
    </div>
  );
}

function MenuContent({
  user,
  onClose,
  onSignOut,
}: {
  user: UserData;
  onClose: () => void;
  onSignOut: () => void;
}) {
  const { t } = useTranslation(NS.navbarMenus);
  const history = useHistory();

  return (
    <motion.div
      layoutId="hiacuity-user-btn"
      transition={SPRING}
      style={{ borderRadius: 20 }}
      className="absolute top-0 right-0 z-[9999] w-72 sm:w-80 bg-[#f8fafc] border border-slate-200/90 shadow-[0_20px_50px_rgba(0,0,0,0.16)] backdrop-blur-xl overflow-hidden p-2 text-slate-800"
    >
      {/* Header section with large avatar */}
      <div className="flex flex-col items-center justify-center pt-3 pb-3 px-2">
        <div className="mb-2">
          <Avatar large user={user} />
        </div>

        <motion.div
          className="text-center w-full px-2"
          initial={contentAnimations.initial}
          animate={contentAnimations.animate}
          exit={contentAnimations.exit}
        >
          <h3 className="text-sm font-bold text-slate-900 truncate">
            {user.name}
          </h3>
          <p className="text-xs text-slate-500 font-medium truncate mt-0.5">
            {user.email}
          </p>

          <div className="flex items-center justify-center gap-2 mt-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100/80 text-emerald-800 border border-emerald-200/60">
              {translateRole(t, user.role)}
            </span>
            <div className="flex items-center gap-1 text-[11px] font-medium text-emerald-700">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>{t(NAVBAR_MENU_KEYS.profile.online)}</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions (View Switcher) */}
      <motion.div
        className="px-1.5 pb-2"
        initial={contentAnimations.initial}
        animate={contentAnimations.animate}
        exit={contentAnimations.exit}
      >
        <div className="flex items-center justify-between px-1 mb-1">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
            {t(NAVBAR_MENU_KEYS.profile.quickActions)}
          </span>
        </div>
        <ViewOption />
      </motion.div>

      {/* Menu items inner card */}
      <motion.div
        className="bg-white rounded-2xl p-1.5 shadow-xs border border-slate-200/70 flex flex-col gap-0.5"
        initial={contentAnimations.initial}
        animate={contentAnimations.animate}
        exit={contentAnimations.exit}
      >
        <MenuItem
          icon={<UserIcon size={16} />}
          title={t(NAVBAR_MENU_KEYS.profile.items.profileSettings.title)}
          description={t(
            NAVBAR_MENU_KEYS.profile.items.profileSettings.description,
          )}
          onClick={() => {
            onClose();
            history.push('/user-profile');
          }}
        />

        <MenuItem
          icon={<LockKeyholeIcon size={16} />}
          title={t(NAVBAR_MENU_KEYS.profile.items.changePassword.title)}
          description={t(
            NAVBAR_MENU_KEYS.profile.items.changePassword.description,
          )}
          onClick={() => {
            onClose();
            history.push('/change-password');
          }}
        />

        <div className="my-1 border-t border-slate-100" />

        <MenuItem
          isDestructive
          icon={<LogoutIcon size={16} />}
          title={t(NAVBAR_MENU_KEYS.profile.signOut.title)}
          description={t(NAVBAR_MENU_KEYS.profile.signOut.description)}
          onClick={() => {
            onClose();
            onSignOut();
          }}
        />
      </motion.div>

      {/* HiAcuity Footer Credit */}
      <motion.div
        className="pt-2.5 pb-1 flex items-center justify-center gap-1 text-[11px] font-medium text-slate-400"
        initial={contentAnimations.initial}
        animate={contentAnimations.animate}
        exit={contentAnimations.exit}
      >
        <span>HiAcuity Workspace</span>
      </motion.div>
    </motion.div>
  );
}

export const Profile: React.FC = () => {
  const { t } = useTranslation(NS.navbarMenus);
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Retrieve user details from localStorage with reactive state
  const [profilePic, setProfilePic] = useState<string>(() => {
    const raw = localStorage.getItem('profilePic') || '';
    return !raw || raw === 'null' || raw === 'undefined' || !raw.trim() ? '' : raw.trim();
  });
  const [firstName, setFirstName] = useState<string>(() => localStorage.getItem('firstname') || '');
  const [lastName, setLastName] = useState<string>(() => localStorage.getItem('lastname') || '');
  const [email, setEmail] = useState<string>(() => localStorage.getItem('email') || 'user@example.com');
  const role = localStorage.getItem('role') || 'Candidate';

  useEffect(() => {
    const handleProfileUpdate = (e?: Event) => {
      const customEvent = e as CustomEvent<{ profilePic?: string; firstName?: string; lastName?: string; email?: string }>;
      const newPic = customEvent?.detail?.profilePic ?? (localStorage.getItem('profilePic') || '');
      const cleanPic = !newPic || newPic === 'null' || newPic === 'undefined' || !newPic.trim() ? '' : newPic.trim();
      setProfilePic(cleanPic);

      const fn = customEvent?.detail?.firstName ?? (localStorage.getItem('firstname') || '');
      const ln = customEvent?.detail?.lastName ?? (localStorage.getItem('lastname') || '');
      const em = customEvent?.detail?.email ?? (localStorage.getItem('email') || 'user@example.com');
      setFirstName(fn);
      setLastName(ln);
      setEmail(em);
    };

    window.addEventListener('profilePicUpdated' as any, handleProfileUpdate);
    window.addEventListener('userProfileUpdated' as any, handleProfileUpdate);
    window.addEventListener('storage', handleProfileUpdate);

    return () => {
      window.removeEventListener('profilePicUpdated' as any, handleProfileUpdate);
      window.removeEventListener('userProfileUpdated' as any, handleProfileUpdate);
      window.removeEventListener('storage', handleProfileUpdate);
    };
  }, []);

  const user: UserData = {
    name:
      `${firstName} ${lastName}`.trim() ||
      t(NAVBAR_MENU_KEYS.profile.defaultUserName),
    avatar: profilePic,
    initials:
      `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U',
    role: role || 'User',
    email,
  };

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Logout function
  const logOut = async () => {
    await logout();
    history.push('/login');
  };

  return (
    <div className="relative w-10 h-10" ref={rootRef}>
      <AnimatePresence>
        {!open ? (
          <motion.button
            key="closed"
            layoutId="hiacuity-user-btn"
            transition={SPRING}
            style={{ borderRadius: 9999 }}
            onClick={() => setOpen(true)}
            aria-label="Open profile menu"
            className="absolute top-0 right-0 w-10 h-10 rounded-full flex items-center justify-center cursor-pointer border-0 p-0 overflow-hidden bg-transparent shadow-sm hover:scale-105 transition-transform duration-150 focus:outline-none"
          >
            <Avatar user={user} />
          </motion.button>
        ) : (
          <MenuContent
            key="open"
            user={user}
            onClose={() => setOpen(false)}
            onSignOut={logOut}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
